import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FoxyAvatar } from './FoxyAvatar';

/** Digest data matching the aggregator output */
export interface DigestData {
  readonly orders: { readonly count: number; readonly revenue: number };
  readonly newCustomers: number;
  readonly topProduct: string | null;
  readonly bookings: { readonly count: number; readonly upcoming: number };
  readonly messages: { readonly received: number; readonly autoAnswered: number };
}

interface FoxyDigestProps {
  readonly digest: DigestData | null;
  readonly loading: boolean;
}

function formatNIS(agorot: number): string {
  return (agorot / 100).toLocaleString('he-IL', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Compact digest preview card for the admin dashboard.
 *
 * Shows today's stats (orders, revenue, top product, customers, bookings).
 * RTL Hebrew layout with shadcn/ui Card, small FoxyAvatar in header.
 *
 * Handles 3 states: loading (skeleton), empty (no data message), data (stats grid).
 */
export function FoxyDigest({ digest, loading }: FoxyDigestProps) {
  return (
    <Card className="w-full" dir="rtl">
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <FoxyAvatar state="idle" size={32} />
        <CardTitle className="text-base">
          {'\u05E1\u05D9\u05DB\u05D5\u05DD \u05D9\u05D5\u05DE\u05D9'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading && <DigestSkeleton />}
        {!loading && !digest && <DigestEmpty />}
        {!loading && digest && <DigestStats data={digest} />}
      </CardContent>
    </Card>
  );
}

function DigestSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-5 w-3/4 animate-pulse rounded bg-muted" />
      ))}
    </div>
  );
}

function DigestEmpty() {
  return (
    <p className="text-sm text-muted-foreground">
      {'\u05D0\u05D9\u05DF \u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05DC\u05D4\u05E6\u05D2\u05D4'}
    </p>
  );
}

function DigestStats({ data }: { readonly data: DigestData }) {
  const items: readonly { label: string; value: string }[] = [
    ...(data.orders.count > 0
      ? [
          { label: '\u05D4\u05D6\u05DE\u05E0\u05D5\u05EA', value: data.orders.count.toLocaleString('he-IL') },
          { label: '\u05D4\u05DB\u05E0\u05E1\u05D4', value: formatNIS(data.orders.revenue) },
        ]
      : []),
    ...(data.topProduct
      ? [{ label: '\u05DE\u05D5\u05D1\u05D9\u05DC \u05DE\u05D5\u05D1\u05D9\u05DC', value: data.topProduct }]
      : []),
    ...(data.newCustomers > 0
      ? [{ label: '\u05DC\u05E7\u05D5\u05D7\u05D5\u05EA \u05D7\u05D3\u05E9\u05D9\u05DD', value: data.newCustomers.toLocaleString('he-IL') }]
      : []),
    ...(data.bookings.count > 0
      ? [{ label: '\u05EA\u05D5\u05E8\u05D9\u05DD', value: `${data.bookings.count.toLocaleString('he-IL')} (\u05E7\u05E8\u05D5\u05D1\u05D9\u05DD: ${data.bookings.upcoming})` }]
      : []),
    ...(data.messages.received > 0
      ? [{ label: '\u05D4\u05D5\u05D3\u05E2\u05D5\u05EA', value: `${data.messages.received} (\u05D0\u05D5\u05D8\u05D5\u05DE\u05D8\u05D9: ${data.messages.autoAnswered})` }]
      : []),
  ];

  if (items.length === 0) {
    return <DigestEmpty />;
  }

  return (
    <div className="grid grid-cols-2 gap-2">
      {items.map((item) => (
        <div key={item.label} className="space-y-0.5">
          <p className="text-xs text-muted-foreground">{item.label}</p>
          <p className="text-sm font-semibold">{item.value}</p>
        </div>
      ))}
    </div>
  );
}
